import { api } from '@/utils/apiService';
import { backendUrl } from '@/utils/backendUrl';
import { normalizeCompanyImageMeta } from '@/utils/companyImageMeta';

const profile = {
  namespaced: true,
  state: {
    companyData: null,
    companyID: null,
    name: null,
    info: null,
    mobile: null,
    contactMail: null,
    website: null,
    instagram: null,
    googleReviewUrl: null,
    deliveryLieferandoUrl: null,
    deliveryUbereatsUrl: null,
    deliveryWoltUrl: null,
    address: null,
    position: null,
    more: null,
    subscription: null,
    boost: null,
    verified: 0,
    categoryID: null,
    priceRange: null,
    smokingAllowed: null,
    dresscodeRequired: null,
    dresscodeInfo: null,
    companyImages: [],
    companyImageMeta: [],
    companyMenu: [],
    specialDays: [],
    usedCompanyTags: [],
    companyRallies: [],
    isLoadingVisitors: false,
    isLoadingRallies: false,
    isLoadingSignups: false,
    isLoadingFavorites: false,
    signups: {
      total: 0,
    },
    inviteQuota: null,
    friendsQuota: null,
    preparationPendingCount: 0,
    ownerPinStatus: {
      enabled: false,
      requestStatus: 'none',
      setupPending: false,
    },
    ownerPinStatusKnown: false,
    favorites: {
      total: 0,
    },
    visitors: {
      total: 0,
      periodTotal: 0,
      periodDelta: 0,
      labels: [],
      values: [],
      period: 'week',
      offset: 0,
      highlightIndex: -1,
      daily: [],
      currentWeekTotal: 0,
    },
    latitude: null,
    longitude: null,
    allTags: [],
    monday: {
      active: false, info: '', name: '', times: [],
    },
    tuesday: {
      active: false, info: '', name: '', times: [],
    },
    wednesday: {
      active: false, info: '', name: '', times: [],
    },
    thursday: {
      active: false, info: '', name: '', times: [],
    },
    friday: {
      active: false, info: '', name: '', times: [],
    },
    saturday: {
      active: false, info: '', name: '', times: [],
    },
    sunday: {
      active: false, info: '', name: '', times: [],
    },
  },
  mutations: {
    SET_COMPANY: (state, company) => {
      state.companyData = company;
      state.companyID = company.companyID;
      state.name = company.name;
      state.info = company.info;
      state.mobile = company.mobile;
      state.contactMail = company.email;
      state.address = company.address;
      state.website = company.website;
      state.instagram = company.instagram;
      state.googleReviewUrl = company.googleReviewUrl ?? null;
      state.deliveryLieferandoUrl = company.deliveryLieferandoUrl ?? null;
      state.deliveryUbereatsUrl = company.deliveryUbereatsUrl ?? null;
      state.deliveryWoltUrl = company.deliveryWoltUrl ?? null;
      state.categoryID = company.categoryID;
      state.priceRange = company.priceRange ?? null;
      state.smokingAllowed = company.smokingAllowed ?? null;
      state.dresscodeRequired = company.dresscodeRequired ?? null;
      state.dresscodeInfo = company.dresscodeInfo ?? null;
      state.companyImages = company.companyImages || [];
      state.companyImageMeta = normalizeCompanyImageMeta(company.companyImageMeta);
      state.companyMenu = company.more;
      state.specialDays = company.specialDay;
      state.latitude = company.latitude;
      state.longitude = company.longitude;
      state.usedCompanyTags = company.tags;
      state.monday = company.monday;
      state.tuesday = company.tuesday;
      state.thursday = company.thursday;
      state.wednesday = company.wednesday;
      state.friday = company.friday;
      state.saturday = company.saturday;
      state.sunday = company.sunday;
    },
    SET_COMPANY_TAGS: (state, tags) => {
      state.usedCompanyTags = tags;
    },
    SET_ALL_TAGS: (state, allTags) => {
      state.allTags = allTags;
    },
    ADD_IMAGE: (state, image) => {
      const index = state.companyImages.findIndex(e => e === image);
      if (index === -1) {
        state.companyImages.push(image);
      } else {
        state.companyImages.splice(index, 1, image);
      }
    },
    REMOVE_IMAGE: (state, image) => {
      const index = state.companyImages.findIndex(e => e === image);
      if (index === -1) return;
      state.companyImages.splice(index, 1);
      if (state.companyImageMeta.length > index) {
        state.companyImageMeta.splice(index, 1);
      }
    },
    SET_COMPANY_IMAGES: (state, { companyImages, companyImageMeta }) => {
      state.companyImages = companyImages || [];
      state.companyImageMeta = normalizeCompanyImageMeta(companyImageMeta);
      const primaryRow = state.companyImageMeta.find((row) => row.isPrimary);
      const primaryCompanyImageId = primaryRow?.imageId ?? null;
      if (state.companyData) {
        state.companyData = {
          ...state.companyData,
          companyImages: state.companyImages,
          companyImageMeta: state.companyImageMeta,
          primaryCompanyImageId,
        };
      }
    },
    USE_TAG: (state, tag) => {
      const index = state.allTags.findIndex(item => item.name === tag.name);
      if (index !== -1) {
        state.usedCompanyTags.push(tag);
      }
      if (index === -1) {
        state.usedCompanyTags.push(tag);
      }
    },
    REMOVE_TAG: (state, tag) => {
      const index = state.usedCompanyTags.findIndex(item => item.name === tag.name);
      if (index !== -1) {
        state.usedCompanyTags.splice(index, 1);
      }
    },
    SET_MENU: (state, list) => {
      state.companyMenu = list;
    },
    SET_COMPANY_RALLIES: (state, rallies) => {
      state.companyRallies = rallies;
    },
    SET_VISITORS: (state, visitors) => {
      state.visitors = visitors;
    },
    SET_LOADING_VISITORS: (state, isLoading) => {
      state.isLoadingVisitors = isLoading;
    },
    SET_LOADING_RALLIES: (state, isLoading) => {
      state.isLoadingRallies = isLoading;
    },
    SET_SIGNUPS: (state, signups) => {
      state.signups = signups;
    },
    SET_SIGNUP_COUNT: (state, total) => {
      if (typeof total === 'number') {
        state.signups = { ...state.signups, total };
      }
    },
    SET_INVITE_QUOTA: (state, quota) => {
      state.inviteQuota = quota && typeof quota === 'object' ? quota : null;
    },
    SET_FRIENDS_QUOTA: (state, quota) => {
      state.friendsQuota = quota && typeof quota === 'object' ? quota : null;
    },
    SET_PREPARATION_PENDING_COUNT: (state, count) => {
      state.preparationPendingCount = typeof count === 'number' ? count : 0;
    },
    SET_VISIT_TOTAL: (state, total) => {
      if (typeof total === 'number') {
        state.visitors = { ...state.visitors, total };
      }
    },
    SET_LOADING_SIGNUPS: (state, isLoading) => {
      state.isLoadingSignups = isLoading;
    },
    SET_FAVORITES: (state, favorites) => {
      state.favorites = favorites;
    },
    SET_LOADING_FAVORITES: (state, isLoading) => {
      state.isLoadingFavorites = isLoading;
    },
    UPDATE_VOID_PIN_STATUS: (state, { enabled, setupPending, isDefaultPin }) => {
      if (!state.companyData) return;
      state.companyData = {
        ...state.companyData,
        voidPinEnabled: enabled,
        voidPinSetupPending: setupPending,
        voidPinIsDefault: isDefaultPin !== undefined ? Boolean(isDefaultPin) : state.companyData.voidPinIsDefault,
      };
    },
    SET_OWNER_PIN_STATUS: (state, status) => {
      state.ownerPinStatusKnown = true;
      state.ownerPinStatus = {
        enabled: Boolean(status?.enabled),
        requestStatus: status?.requestStatus || 'none',
        setupPending: Boolean(status?.setupPending),
        requestedAt: status?.requestedAt ?? null,
        requestNote: status?.requestNote ?? null,
      };
    },
    SET_SPECIAL_DAYS: (state, specialDays) => {
      state.specialDays = specialDays;
    },
    SET_NAME: (state, name) => {
      state.name = name;
    },
    SET_INFO: (state, info) => {
      state.info = info;
    },
    SET_ADDRESS: (state, address) => {
      state.address = address;
    },
    SET_CONTACT_MAIL: (state, mail) => {
      state.contactMail = mail;
    },
    SET_MOBILE: (state, phone) => {
      state.mobile = phone;
    },
    SET_WEBSITE: (state, website) => {
      state.website = website;
    },
    SET_INSTAGRAM: (state, instagram) => {
      state.instagram = instagram;
    },
    SET_GOOGLE_REVIEW_URL: (state, googleReviewUrl) => {
      state.googleReviewUrl = googleReviewUrl;
    },
    SET_DELIVERY_LIEFERANDO_URL: (state, deliveryLieferandoUrl) => {
      state.deliveryLieferandoUrl = deliveryLieferandoUrl;
    },
    SET_DELIVERY_UBEREATS_URL: (state, deliveryUbereatsUrl) => {
      state.deliveryUbereatsUrl = deliveryUbereatsUrl;
    },
    SET_DELIVERY_WOLT_URL: (state, deliveryWoltUrl) => {
      state.deliveryWoltUrl = deliveryWoltUrl;
    },
    SET_CATEGORY_ID: (state, categoryID) => {
      state.categoryID = categoryID;
    },
    SET_PRICE_RANGE: (state, priceRange) => {
      state.priceRange = priceRange;
    },
    SET_SMOKING_ALLOWED: (state, smokingAllowed) => {
      state.smokingAllowed = smokingAllowed;
    },
    SET_DRESSCODE_REQUIRED: (state, dresscodeRequired) => {
      state.dresscodeRequired = dresscodeRequired;
    },
    SET_DRESSCODE_INFO: (state, dresscodeInfo) => {
      state.dresscodeInfo = dresscodeInfo;
    },
    CLEAR_SESSION: (state) => {
      state.companyData = null;
      state.companyID = null;
      state.name = null;
      state.info = null;
      state.mobile = null;
      state.contactMail = null;
      state.website = null;
      state.instagram = null;
      state.googleReviewUrl = null;
      state.deliveryLieferandoUrl = null;
      state.deliveryUbereatsUrl = null;
      state.deliveryWoltUrl = null;
      state.address = null;
      state.position = null;
      state.more = null;
      state.subscription = null;
      state.boost = null;
      state.verified = 0;
      state.categoryID = null;
      state.priceRange = null;
      state.smokingAllowed = null;
      state.dresscodeRequired = null;
      state.dresscodeInfo = null;
      state.companyImages = [];
      state.companyMenu = [];
      state.specialDays = [];
      state.usedCompanyTags = [];
      state.companyRallies = [];
      state.latitude = null;
      state.longitude = null;
      state.signups = { total: 0 };
      state.favorites = { total: 0 };
      state.visitors = {
        total: 0,
        periodTotal: 0,
        periodDelta: 0,
        labels: [],
        values: [],
        period: 'week',
        offset: 0,
        highlightIndex: -1,
        daily: [],
        currentWeekTotal: 0,
      };
      state.preparationPendingCount = 0;
      state.ownerPinStatus = {
        enabled: false,
        requestStatus: 'none',
        setupPending: false,
      };
      state.ownerPinStatusKnown = false;
    },
    SET_ALL_CATEGORIES: (state, categories) => {
      state.allCategories = categories;
    },
  },
  getters: {
    getCompany: state => state.companyData,
    getCompanyID: state => state.companyID,
    getCompanyImages: state => state.companyImages,
    getCompanyImageMeta: state => state.companyImageMeta,
    getCompanyMenu: state => state.companyMenu,
    getCompanySpecialDays: state => state.specialDays,
    getName: state => state.name,
    getInfo: state => state.info,
    getMobile: state => state.mobile,
    getContactMail: state => state.contactMail,
    getWebsite: state => state.website,
    getInstagram: state => state.instagram,
    getGoogleReviewUrl: state => state.googleReviewUrl,
    getDeliveryLieferandoUrl: state => state.deliveryLieferandoUrl,
    getDeliveryUbereatsUrl: state => state.deliveryUbereatsUrl,
    getDeliveryWoltUrl: state => state.deliveryWoltUrl,
    getCategoryID: state => state.categoryID,
    getPriceRange: state => state.priceRange,
    getSmokingAllowed: state => state.smokingAllowed,
    getDresscodeRequired: state => state.dresscodeRequired,
    getDresscodeInfo: state => state.dresscodeInfo,
    getAddress: state => state.address,
    getPosition: state => state.position,
    getMore: state => state.more,
    getLatitude: state => state.latitude,
    getLongitude: state => state.longitude,
    getAllTags: state => state.allTags,
    getAllCategories: state => state.allCategories,
    getUsedTags: state => state.usedCompanyTags,
    getCompanyRallies: state => state.companyRallies,
    getVisitors: state => state.visitors,
    getSignups: state => state.signups,
    getInviteQuota: state => state.inviteQuota,
    getFriendsQuota: state => state.friendsQuota,
    getPreparationPendingCount: (state) => state.preparationPendingCount,
    ownerPinStatus: (state) => state.ownerPinStatus,
    ownerPinStatusKnown: (state) => state.ownerPinStatusKnown,
    getFavorites: state => state.favorites,
    isLoadingVisitors: state => state.isLoadingVisitors,
    isLoadingSignups: state => state.isLoadingSignups,
    isLoadingFavorites: state => state.isLoadingFavorites,
    isLoadingRallies: state => state.isLoadingRallies,
    isLoggedIn: state => !!state.companyID,
  },
  actions: {
    refreshLiveCounts: async () => {
      const { default: store } = await import('@/store');
      const { refreshCompanyLiveCounts } = await import('@/utils/liveSync');
      return refreshCompanyLiveCounts(store);
    },
    requestCompany: async ({ commit }) => {
      try {
        const response = await api.post('/company/login');
        await commit('SET_COMPANY', response.data.company);
        return response;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    fetchVoidPinStatus: async ({ commit }) => {
      try {
        const response = await api.get('/company/void-pin/status');
        if (response?.status === 200) {
          await commit('UPDATE_VOID_PIN_STATUS', {
            enabled: Boolean(response.data?.enabled),
            setupPending: Boolean(response.data?.setupPending),
            isDefaultPin: Boolean(response.data?.isDefaultPin),
          });
        }
        return response;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    fetchOwnerPinStatus: async ({ commit }) => {
      try {
        const response = await api.get('/company/owner-pin/status');
        if (response?.status === 200) {
          await commit('SET_OWNER_PIN_STATUS', response.data);
        }
        return response;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    requestCompanyTags: async ({ commit }) => {
      try {
        const response = await api.get('/tag/company');
        await commit('SET_COMPANY_TAGS', response.data);
        return response;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    requestAllTags: async ({ commit }) => {
      try {
        const response = await api.get('/tag/all');
        await commit('SET_ALL_TAGS', response.data);
        return response;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    requestAllCategories: async ({ commit }) => {
      try {
        const response = await api.get('/category/all?onlyActive=true');
        await commit('SET_ALL_CATEGORIES', response.data);
        return response;
      } catch (e) {
        console.log(e);
        return null;
      }
    },

    requestCompanyRallies: async ({ commit, getters }) => {
      const companyID = getters.getCompanyID;
      if (!companyID) return null;

      commit('SET_LOADING_RALLIES', true);
      try {
        const response = await api.get(`/company/${companyID}/rallies`);
        if (response && response.status === 200 && Array.isArray(response.data)) {
          await commit('SET_COMPANY_RALLIES', response.data);
        } else {
          await commit('SET_COMPANY_RALLIES', []);
        }
        return response;
      } catch (e) {
        console.log(e);
        await commit('SET_COMPANY_RALLIES', []);
        return null;
      } finally {
        commit('SET_LOADING_RALLIES', false);
      }
    },
    swapCompanySpecial: async ({ dispatch, getters }, { rallyID, newSpecialID }) => {
      try {
        const companyID = getters.getCompanyID;
        if (!companyID) return null;
        const response = await api.patch(`/company/${companyID}/rallies/${rallyID}/swap-special`, { newSpecialID });
        if (response.status === 200) {
          // Refresh the rallies to get the updated stations list
          await dispatch('requestCompanyRallies');
        }
        return response;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    requestCompanyVisitors: async ({ commit, getters }, payload = {}) => {
      const companyID = getters.getCompanyID;
      if (!companyID) return null;

      const period = payload.period || 'week';
      const offset = payload.offset ?? payload.weeksAgo ?? 0;

      commit('SET_LOADING_VISITORS', true);
      try {
        const response = await api.get(
          `/company/${companyID}/visitors?period=${period}&offset=${offset}`,
        );
        if (response && response.status === 200 && response.data) {
          await commit('SET_VISITORS', response.data);
        } else {
          await commit('SET_VISITORS', {
            total: 0,
            periodTotal: 0,
            periodDelta: 0,
            labels: [],
            values: [],
            period,
            offset,
            highlightIndex: -1,
            daily: [],
            currentWeekTotal: 0,
          });
        }
        return response;
      } catch (e) {
        console.log(e);
        await commit('SET_VISITORS', {
          total: 0,
          periodTotal: 0,
          periodDelta: 0,
          labels: [],
          values: [],
          period: 'week',
          offset: 0,
          highlightIndex: -1,
          daily: [],
          currentWeekTotal: 0,
        });
        return null;
      } finally {
        commit('SET_LOADING_VISITORS', false);
      }
    },
    requestCompanySignups: async ({ commit, getters }) => {
      const companyID = getters.getCompanyID;
      if (!companyID) return null;

      commit('SET_LOADING_SIGNUPS', true);
      try {
        const response = await api.get(`/company/${companyID}/signups`);
        if (response && response.status === 200 && response.data) {
          await commit('SET_SIGNUPS', response.data);
        } else {
          await commit('SET_SIGNUPS', { total: 0 });
        }
        return response;
      } catch (e) {
        console.log(e);
        await commit('SET_SIGNUPS', { total: 0 });
        return null;
      } finally {
        commit('SET_LOADING_SIGNUPS', false);
      }
    },
    requestCompanyFavorites: async ({ commit, getters }) => {
      const companyID = getters.getCompanyID;
      if (!companyID) return null;

      commit('SET_LOADING_FAVORITES', true);
      try {
        const response = await api.get(`/company/${companyID}/favorites`);
        if (response?.status === 200 && typeof response.data?.total === 'number') {
          await commit('SET_FAVORITES', { total: response.data.total });
        } else {
          await commit('SET_FAVORITES', { total: 0 });
        }
        return response;
      } catch (e) {
        console.log(e);
        await commit('SET_FAVORITES', { total: 0 });
        return null;
      } finally {
        commit('SET_LOADING_FAVORITES', false);
      }
    },
    setCompany: async ({ commit }, company) => {
      await commit('SET_COMPANY', company);
    },
    removeImageFromCompany: async ({ dispatch }, img) => {
      const n = img.split('/');
      const response = await api.delete(`/image/company/${n[n.length - 1]}`);
      if (response?.status === 200 || response?.status === 204) {
        await dispatch('refreshCompanyImages');
      }
      return response;
    },
    addImageToCompany: async ({ commit }, img) => {
      await commit('ADD_IMAGE', img);
    },
    refreshCompanyImages: async ({ commit }) => {
      try {
        const response = await api.get('/image/company');
        if (!response?.data || !Array.isArray(response.data)) return response;

        const companyImages = response.data.map(
          (row) => backendUrl(`/image/company/${row.id}`),
        );
        const companyImageMeta = response.data.map((row) => ({
          imageId: row.id,
          isPrimary: Boolean(row.isPrimary),
          isLogo: Boolean(row.isLogo),
          logoVariant: row.logoVariant || null,
        }));

        await commit('SET_COMPANY_IMAGES', { companyImages, companyImageMeta });
        return response;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    setPrimaryCompanyImage: async ({ dispatch }, imageId) => {
      const response = await api.patch(`/image/company/${imageId}/primary`);
      if (response?.status === 200) {
        await dispatch('refreshCompanyImages');
      }
      return response;
    },
    useTag: async ({ commit }, tag) => {
      await commit('USE_TAG', tag);
    },
    removeTag: async ({ commit }, tag) => {
      await commit('REMOVE_TAG', tag);
    },
    safeTags: async ({ getters }) => {
      const tags = getters.getUsedTags.map(item => item.id);
      await api.post('/tag/company', { tagIDs: tags });
    },
    setCompanyMenu: async ({ commit }, menu) => {
      await commit('SET_MENU', menu);
    },
    safeCompanyMenu: async ({ dispatch, getters }, menu) => {
      const response = await api.put(`/company/${getters.getCompanyID}/menu`, { more: menu });
      if (response.status === 200) {
        dispatch('setCompanyMenu', menu);
      }
    },
    setProfileInfos: async ({ dispatch }, info) => {
      dispatch('setName', info.name);
      dispatch('setInfo', info.description);
      dispatch('setAddress', info.address);
      dispatch('setContactMail', info.email);
      dispatch('setMobile', info.phone);
      dispatch('setWebsite', info.website);
      dispatch('setInstagram', info.instagram);
      dispatch('setGoogleReviewUrl', info.googleReviewUrl ?? null);
      dispatch('setDeliveryLieferandoUrl', info.deliveryLieferandoUrl ?? null);
      dispatch('setDeliveryUbereatsUrl', info.deliveryUbereatsUrl ?? null);
      dispatch('setDeliveryWoltUrl', info.deliveryWoltUrl ?? null);
      dispatch('setCategoryID', info.categoryID);
      dispatch('setPriceRange', info.priceRange ?? null);
      dispatch('setSmokingAllowed', info.smokingAllowed ?? null);
      dispatch('setDresscodeRequired', info.dresscodeRequired ?? null);
      dispatch('setDresscodeInfo', info.dresscodeInfo ?? null);
    },
    safeProfileInfos: async ({ getters, dispatch }, info) => {
      // Backend expects flat attributes: name, info, address, email, mobile, website, instagram
      const payload = {
        name: info.name,
        info: info.description,
        address: info.address,
        email: info.email,
        mobile: info.phone,
        website: info.website,
        instagram: info.instagram,
        googleReviewUrl: info.googleReviewUrl?.trim() || null,
        deliveryLieferandoUrl: info.deliveryLieferandoUrl?.trim() || null,
        deliveryUbereatsUrl: info.deliveryUbereatsUrl?.trim() || null,
        deliveryWoltUrl: info.deliveryWoltUrl?.trim() || null,
        categoryID: info.categoryID,
        priceRange: info.priceRange ?? null,
        smokingAllowed: info.smokingAllowed ?? null,
        dresscodeRequired: info.dresscodeRequired ?? null,
        dresscodeInfo: info.dresscodeInfo?.trim() ? info.dresscodeInfo.trim() : null,
      };
      const response = await api.put(`/company/${getters.getCompanyID}/infos`, payload);
      if (response.status === 200) {
        dispatch('setProfileInfos', info);
      }
    },
    setName: async ({ commit }, name) => {
      await commit('SET_NAME', name);
    },
    setInfo: async ({ commit }, info) => {
      await commit('SET_INFO', info);
    },
    setAddress: async ({ commit }, address) => {
      await commit('SET_ADDRESS', address);
    },
    setContactMail: async ({ commit }, mail) => {
      await commit('SET_CONTACT_MAIL', mail);
    },
    setMobile: async ({ commit }, phone) => {
      await commit('SET_MOBILE', phone);
    },
    setWebsite: async ({ commit }, website) => {
      await commit('SET_WEBSITE', website);
    },
    setInstagram: async ({ commit }, instagram) => {
      await commit('SET_INSTAGRAM', instagram);
    },
    setGoogleReviewUrl: async ({ commit }, googleReviewUrl) => {
      await commit('SET_GOOGLE_REVIEW_URL', googleReviewUrl);
    },
    setDeliveryLieferandoUrl: async ({ commit }, deliveryLieferandoUrl) => {
      await commit('SET_DELIVERY_LIEFERANDO_URL', deliveryLieferandoUrl);
    },
    setDeliveryUbereatsUrl: async ({ commit }, deliveryUbereatsUrl) => {
      await commit('SET_DELIVERY_UBEREATS_URL', deliveryUbereatsUrl);
    },
    setDeliveryWoltUrl: async ({ commit }, deliveryWoltUrl) => {
      await commit('SET_DELIVERY_WOLT_URL', deliveryWoltUrl);
    },
    setCategoryID: async ({ commit }, categoryID) => {
      await commit('SET_CATEGORY_ID', categoryID);
    },
    setPriceRange: async ({ commit }, priceRange) => {
      await commit('SET_PRICE_RANGE', priceRange);
    },
    setSmokingAllowed: async ({ commit }, smokingAllowed) => {
      await commit('SET_SMOKING_ALLOWED', smokingAllowed);
    },
    setDresscodeRequired: async ({ commit }, dresscodeRequired) => {
      await commit('SET_DRESSCODE_REQUIRED', dresscodeRequired);
    },
    setDresscodeInfo: async ({ commit }, dresscodeInfo) => {
      await commit('SET_DRESSCODE_INFO', dresscodeInfo);
    },
    setSpecialDays: async ({ commit }, specialDays) => {
      await commit('SET_SPECIAL_DAYS', specialDays);
    },
    safeSpecialDays: async ({ dispatch, getters }, specialDays) => {
      // Backend expects flat attributes: monday, tuesday, etc.
      // specialDays is exactly the hoursForm which has monday, tuesday, etc.
      const payload = { ...specialDays };
      const response = await api.put(`/company/${getters.getCompanyID}/specialDays`, payload);
      if (response.status === 200) {
        dispatch('setSpecialDays', specialDays);
      }
    },
  },
};

export default profile;
